/**
 * LoginCard — the one sign-in card.
 *
 * Two surfaces show it: the gym app's full-page login (`App.js`, when there is
 * no `authUser`) and the gym selector's login modal (`GymSelector.js`). Before
 * M18 they were two copies of the same ~80 lines, byte-identical down to the
 * `border: "2px solid " + th.border` on the provider buttons — which is exactly
 * how they drifted without anyone noticing: the auth error under the provider
 * buttons is `textAlign: "left"` in the app and inherited-centre in the selector,
 * because only one copy got the fix. See the alignment note below.
 *
 * The card is part of the component, not the caller's job: both call sites wrote
 * the same `padding: 32, width: "100%", maxWidth: 380, textAlign: "center"` Card,
 * and a copy that owns only the form leaves that line duplicated for the next
 * person to drift.
 *
 * **Alignment.** Both containers centre their text, so everything inside is
 * centred unless it says otherwise. The two blocks that must not be — the auth
 * error and the password section's labels — say so explicitly here rather than
 * relying on what the caller's Card happens to do.
 *
 * The auth state and handlers arrive as one `auth` object rather than eighteen
 * props. That is the shape `useAuth()` returns, and since N1 both call sites get
 * it from that hook — the selector with `gymId: null`.
 */
import React from "react";
import { F } from "../constants";
import { Card, GoogleIcon, AppleIcon, IconClose, IconBtn, TextInput } from "./ui";
import { useTranslation } from "../i18n";
import { useAppTheme } from "../contexts/ThemeContext";

/** Google / Apple: same button, one icon apart. */
const providerBtn = (th) => ({
  display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%",
  padding: "13px 20px", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer",
  fontFamily: F, border: "2px solid " + th.border,
});

/** The filled accent button, greyed while its input is empty. */
const submitBtn = (th, accent, enabled, { padding = "12px 18px", fontSize, fontWeight = 700, flex, whiteSpace, cursor } = {}) => ({
  ...(flex === undefined ? null : { flex }),
  padding, borderRadius: 10, fontSize, fontWeight,
  cursor: cursor || (enabled ? "pointer" : "default"),
  background: enabled ? accent : th.border, color: "#fff", border: "none", fontFamily: F,
  ...(whiteSpace === undefined ? null : { whiteSpace }),
  opacity: enabled ? 1 : 0.5,
});

export function LoginCard({ auth, onClose, hint }) {
  const { th, accent } = useAppTheme();
  const { t } = useTranslation();
  const {
    emailInput, setEmailInput, passwordInput, setPasswordInput,
    emailSent, setEmailSent, authError, setAuthError,
    needsEmailConfirm, handleConfirmEmail,
    isStandalone, isIOSStandalone, isPasswordAuthEnabled,
    handleLogin, handleAppleLogin, handleEmailLink,
    handlePasswordSignIn, handlePasswordSignUp,
  } = auth;

  const hasEmail = !!emailInput.trim();
  const canPassword = hasEmail && !!passwordInput;

  /**
   * Which control the user last used — and therefore where the next error belongs.
   *
   * `authError` is one string for the whole card, so for a long time every error
   * appeared under the provider buttons: type a malformed address into the field
   * at the BOTTOM, get "invalid email address" at the TOP, above a divider, next
   * to two buttons you did not touch.
   *
   * Classifying by error key cannot fix it — `login.networkError` and
   * `login.signInFailed` are produced by the provider popups AND by the email
   * send. What is unambiguous is which handler the user just invoked, and the
   * card is the thing that invoked it, so it tracks that itself rather than
   * asking the two auth copies to start reporting an origin.
   */
  const [errorFrom, setErrorFrom] = React.useState("provider");
  const from = (where, run) => () => { setErrorFrom(where); run(); };

  /** Never swallow an error: if its section is not on screen, it goes back to
   *  the provider slot, which is always rendered. Defensive rather than live —
   *  both flags are fixed for the life of the card, so no origin can be orphaned
   *  today — but an auth error that renders nowhere is a bad enough failure to be
   *  worth one expression. */
  const errorSlot =
    isIOSStandalone || (errorFrom === "password" && !isPasswordAuthEnabled) ? "provider" : errorFrom;
  const errorAt = (slot) => authError && errorSlot === slot && (
    <div role="alert" aria-live="polite" style={{ fontSize: 12, color: "#dc2626", fontWeight: 600, textAlign: "left" }}>{t(authError)}</div>
  );

  const body = needsEmailConfirm ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 32, marginBottom: 4 }}>✉️</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: th.text }}>{t('login.confirmEmailTitle')}</div>
      <div style={{ fontSize: 13, color: th.textMuted, marginBottom: 6 }}>{t('login.confirmEmailDesc')}</div>
      <TextInput size="lg" type="email" autoFocus inputMode="email" autoComplete="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
        placeholder={t('login.emailPlaceholder')}
        aria-label={t('login.emailPlaceholder')}
        onKeyDown={e => e.key === "Enter" && handleConfirmEmail()}
      />
      {authError && <div role="alert" style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>{t(authError)}</div>}
      <button onClick={handleConfirmEmail} disabled={!hasEmail}
        style={submitBtn(th, accent, hasEmail, { fontSize: 14, fontWeight: 800 })}>{t('login.confirmEmailButton')}</button>
    </div>
  ) : emailSent ? (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>✉️</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: th.text, marginBottom: 8 }}>{t('login.linkSent')}</div>
      <div style={{ fontSize: 13, color: th.textMuted, marginBottom: 16 }}>
        {t('login.checkEmailPrefix')} <strong style={{ color: th.text }}>{emailInput}</strong>{t('login.checkEmailSuffix')}
      </div>
      <button onClick={() => { setEmailSent(false); setAuthError(""); }}
        style={{ ...providerBtn(th), background: "none", color: th.textMuted }}>{t('common.back')}</button>
    </div>
  ) : (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 15, color: th.text, marginBottom: 10, fontWeight: 500 }}>{t('login.title')}</div>
      <button onClick={from("provider", handleLogin)} style={{ ...providerBtn(th), background: th.card, color: th.text }}>
        <GoogleIcon />{t('login.google')}
      </button>
      <button onClick={from("provider", handleAppleLogin)} style={{ ...providerBtn(th), background: th.card, color: th.text }}>
        <AppleIcon />{t('login.apple')}
      </button>
      {/* Provider slot. Also the fallback for everything else, and the only slot
          on iOS standalone — the Apple account-link prompt arrives as an auth
          error and is the sole instruction the user gets there, so it must sit
          OUTSIDE the email gate below. */}
      {errorAt("provider")}
      {!isIOSStandalone && (<>
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "6px 0" }}>
          <div style={{ flex: 1, height: 1, background: th.border }} />
          <span style={{ fontSize: 12, color: th.textMuted }}>{t('login.or')}</span>
          <div style={{ flex: 1, height: 1, background: th.border }} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <TextInput size="lg" invalid={!!authError} type="email" inputMode="email" autoComplete="email" value={emailInput} onChange={e => { setEmailInput(e.target.value); if (authError) setAuthError(""); }}
            placeholder={t('login.emailPlaceholder')}
            aria-label={t('login.emailPlaceholder')}
            onKeyDown={e => e.key === "Enter" && from("email", handleEmailLink)()}
            style={{ flex: 1 }} />
          <button onClick={from("email", handleEmailLink)} disabled={!hasEmail}
            style={submitBtn(th, accent, hasEmail, { fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" })}>{t('login.send')}</button>
        </div>
        {/* Under the field it is about — not at the top of the card. */}
        {errorAt("email")}
        <div style={{ fontSize: 12, color: th.textMuted }}>{isStandalone ? t('login.emailHintPwa') : t('login.emailHint')}</div>
        {isPasswordAuthEnabled && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed " + th.border, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 12, color: th.textMuted, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", textAlign: "left" }}>{t('login.passwordSectionTitle')}</div>
            <TextInput size="lg" type="password" autoComplete="current-password" value={passwordInput}
              onChange={e => { setPasswordInput(e.target.value); if (authError) setAuthError(""); }}
              placeholder={t('login.passwordPlaceholder')}
              aria-label={t('login.passwordPlaceholder')}
              onKeyDown={e => e.key === "Enter" && canPassword && from("password", handlePasswordSignIn)()}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={from("password", handlePasswordSignIn)} disabled={!canPassword}
                style={submitBtn(th, accent, canPassword, { flex: 1, padding: "10px 14px", fontSize: 13 })}>{t('login.passwordSignIn')}</button>
              <button onClick={from("password", handlePasswordSignUp)} disabled={!canPassword}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                  cursor: canPassword ? "pointer" : "default",
                  background: "transparent", color: th.text, border: "2px solid " + th.border, fontFamily: F,
                  opacity: canPassword ? 1 : 0.5,
                }}>{t('login.passwordSignUp')}</button>
            </div>
            {errorAt("password")}
            <div style={{ fontSize: 12, color: th.textMuted, textAlign: "left" }}>{t('login.passwordTestOnlyHint')}</div>
          </div>
        )}
      </>)}
      {hint}
    </div>
  );

  return (
    <Card style={{ padding: 32, width: "100%", maxWidth: 380, textAlign: "center", ...(onClose ? { position: "relative" } : null) }}>
      {onClose && (
        <IconBtn icon={IconClose} tone="muted" size={18} padding={4} title={t('common.close')} onClick={onClose}
          style={{ position: "absolute", top: 12, right: 12 }} />
      )}
      {/* The close button overlaps the first row; the caller that has one needs the clearance. */}
      {onClose ? <div style={{ marginTop: 8 }}>{body}</div> : body}
    </Card>
  );
}
